// Comprehensive Profile page with sidebar navigation
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Settings, FileText, LogOut, Edit3, Camera, MapPin, Calendar } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../hooks/useProfile';
import { HazardReport } from '../types';
import apiService from '../services/api';
import { useNavigate } from 'react-router-dom';
import styles from '../styles/Profile.module.scss';

type ProfileTab = 'overview' | 'reports' | 'settings';

const Profile: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const { profile, uploading, uploadProfilePicture, updateProfileInfo } = useProfile();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<ProfileTab>('overview');
  const [userReports, setUserReports] = useState<HazardReport[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditNameModalOpen, setIsEditNameModalOpen] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState('');
  const [emailNotifications, setEmailNotifications] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Redirect to home if user is not logged in
  useEffect(() => {
    if (!currentUser) {
      navigate('/');
    }
  }, [currentUser, navigate]);

  // Initialize email notifications state from profile
  useEffect(() => {
    if (profile?.emailNotifications !== undefined) {
      setEmailNotifications(profile.emailNotifications);
    }
  }, [profile?.emailNotifications]);

  // Fetch user's reports from backend API
  useEffect(() => {
    const fetchUserReports = async () => {
      if (!currentUser) return;

      try {
        setIsLoading(true);
        const response = await apiService.getReports({
          page: 1,
          limit: 100,
          userId: currentUser.uid,
          sortBy: "createdAt",
          sortOrder: "desc",
        });

        setUserReports(response.data.reports);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching user reports:", error);
        setIsLoading(false);
      }
    };

    fetchUserReports();
  }, [currentUser]);

  const sidebarItems = [
    { id: 'overview', label: 'Overview', icon: User },
    { id: 'reports', label: 'My Reports', icon: FileText },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handlePhotoChange = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    try {
      await uploadProfilePicture(file);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert('Failed to upload photo. Please try again.');
    }
  };

  const handleEditName = () => {
    setNewDisplayName(profile?.displayName || '');
    setIsEditNameModalOpen(true);
  };

  const handleSaveName = async () => {
    if (!newDisplayName.trim()) {
      alert('Display name cannot be empty');
      return;
    }

    try {
      await updateProfileInfo({ displayName: newDisplayName.trim() });
      setIsEditNameModalOpen(false);
    } catch (error) {
      console.error('Error updating name:', error);
      alert('Failed to update name. Please try again.');
    }
  };

  const handleNotificationToggle = async (checked: boolean) => {
    setEmailNotifications(checked);
    try {
      await updateProfileInfo({ emailNotifications: checked });
    } catch (error) {
      console.error('Error updating notifications:', error);
      // Revert on error
      setEmailNotifications(!checked);
      alert('Failed to update notification preference. Please try again.');
    }
  };

  // Don't render if user is not logged in
  if (!currentUser) {
    return null;
  }

  const formatDate = (timestamp: string | Date) => {
    if (!timestamp) return "Unknown";
    const date = new Date(timestamp);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "critical":
        return "#ff4757";
      case "high":
        return "#ff6b35";
      case "medium":
        return "#ffa502";
      case "low":
        return "#26de81";
      default:
        return "#70a1ff";
    }
  };

  const renderOverview = () => (
    <motion.div
      className={styles.tabContent}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
    >
      <div className={styles.overviewGrid}>
        <div className={styles.statsCard}>
          <div className={styles.statIcon}>
            <FileText size={24} />
          </div>
          <div className={styles.statInfo}>
            <h3>{userReports.length}</h3>
            <p>Total Reports</p>
          </div>
        </div>

        <div className={styles.statsCard}>
          <div className={styles.statIcon}>
            <MapPin size={24} />
          </div>
          <div className={styles.statInfo}>
            <h3>{userReports.filter((r) => r.status === "pending").length}</h3>
            <p>Active Reports</p>
          </div>
        </div>

        <div className={styles.statsCard}>
          <div className={styles.statIcon}>
            <Calendar size={24} />
          </div>
          <div className={styles.statInfo}>
            <h3>
              {currentUser?.metadata?.creationTime
                ? new Date(
                    currentUser.metadata.creationTime
                  ).toLocaleDateString("en-US", {
                    month: "short",
                    year: "numeric",
                  })
                : "Unknown"}
            </h3>
            <p>Member Since</p>
          </div>
        </div>
      </div>

      <div className={styles.recentActivity}>
        <h3>Recent Activity</h3>
        {userReports.slice(0, 3).map((report) => (
          <div key={report.id} className={styles.activityItem}>
            <div
              className={styles.severityIndicator}
              style={{ backgroundColor: getSeverityColor(report.severity) }}
            />
            <div className={styles.activityContent}>
              <h4>{report.hazardType.replace("-", " ").toUpperCase()}</h4>
              <p>{report.description.substring(0, 100)}...</p>
              <span className={styles.activityDate}>
                {formatDate(report.createdAt)}
              </span>
            </div>
          </div>
        ))}
        {userReports.length === 0 && (
          <div className={styles.emptyState}>
            <FileText size={48} />
            <p>No reports submitted yet</p>
          </div>
        )}
      </div>
    </motion.div>
  );

  const renderReports = () => (
    <motion.div
      className={styles.tabContent}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
    >
      <div className={styles.reportsHeader}>
        <h3>My Hazard Reports</h3>
        <span className={styles.reportCount}>{userReports.length} reports</span>
      </div>

      <div className={styles.reportsList}>
        {userReports.map((report) => (
          <motion.div
            key={report.id}
            className={styles.reportCard}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className={styles.reportHeader}>
              <div className={styles.reportType}>
                <div
                  className={styles.severityBadge}
                  style={{ backgroundColor: getSeverityColor(report.severity) }}
                >
                  {report.severity}
                </div>
                <h4>{report.hazardType.replace("-", " ").toUpperCase()}</h4>
              </div>
              <span className={styles.reportDate}>
                {formatDate(report.createdAt)}
              </span>
            </div>

            <p className={styles.reportDescription}>{report.description}</p>

            <div className={styles.reportFooter}>
              <div className={styles.reportLocation}>
                <MapPin size={16} />
                <span>
                  {report.latitude?.toFixed(4)}, {report.longitude?.toFixed(4)}
                </span>
              </div>
              <div
                className={`${styles.reportStatus} ${
                  styles[report.status || "pending"]
                }`}
              >
                {report.status || "pending"}
              </div>
            </div>
          </motion.div>
        ))}

        {userReports.length === 0 && (
          <div className={styles.emptyReports}>
            <FileText size={64} />
            <h3>No Reports Yet</h3>
            <p>
              Start contributing to ocean safety by submitting your first hazard
              report.
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );

  const renderSettings = () => (
    <motion.div
      className={styles.tabContent}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
    >
      <div className={styles.settingsSection}>
        <h3>Account Settings</h3>

        <div className={styles.settingItem}>
          <div className={styles.settingInfo}>
            <h4>Profile Picture</h4>
            <p>Update your profile photo</p>
          </div>
          <button 
            className={styles.settingButton}
            onClick={handlePhotoChange}
            disabled={uploading}
          >
            <Camera size={16} />
            {uploading ? 'Uploading...' : 'Change Photo'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </div>
        
        <div className={styles.settingItem}>
          <div className={styles.settingInfo}>
            <h4>Display Name</h4>
            <p>How others see your name</p>
          </div>
          <button 
            className={styles.settingButton}
            onClick={handleEditName}
          >
            <Edit3 size={16} />
            Edit Name
          </button>
        </div>
        
        <div className={styles.settingItem}>
          <div className={styles.settingInfo}>
            <h4>Email Notifications</h4>
            <p>Receive updates about your reports</p>
          </div>
          <label className={styles.toggle}>
            <input 
              type="checkbox" 
              checked={emailNotifications}
              onChange={(e) => handleNotificationToggle(e.target.checked)}
            />
            <span className={styles.slider}></span>
          </label>
        </div>
      </div>

      <div className={styles.dangerZone}>
        <h3>Account Actions</h3>
        <button className={styles.logoutButton} onClick={handleLogout}>
          <LogOut size={16} />
          Sign Out
        </button>
      </div>
    </motion.div>
  );

  return (
    <div className={styles.profilePage}>
      <div className={styles.profileContainer}>
        {/* Sidebar */}
        <motion.div
          className={styles.sidebar}
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className={styles.profileHeader}>
            <div className={styles.avatarContainer}>
              {currentUser?.photoURL ? (
                <img
                  src={currentUser.photoURL}
                  alt="Profile"
                  className={styles.avatar}
                />
              ) : (
                <div className={styles.avatarPlaceholder}>
                  {profile?.displayName?.charAt(0)?.toUpperCase() ||
                    currentUser?.email?.charAt(0)?.toUpperCase() ||
                    "U"}
                </div>
              )}
            </div>
            <div className={styles.userInfo}>
              <h2 className={styles.userName}>
                {profile?.displayName || currentUser?.displayName || 'User'}
              </h2>
              <p className={styles.userEmail}>{currentUser?.email}</p>
            </div>
          </div>

          <nav className={styles.navigation}>
            {sidebarItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  className={`${styles.navItem} ${
                    activeTab === item.id ? styles.active : ""
                  }`}
                  onClick={() => setActiveTab(item.id as ProfileTab)}
                >
                  <Icon size={20} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
        </motion.div>

        {/* Main Content */}
        <motion.div
          className={styles.mainContent}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <AnimatePresence mode="wait">
            {activeTab === "overview" && renderOverview()}
            {activeTab === "reports" && renderReports()}
            {activeTab === "settings" && renderSettings()}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Edit Name Modal */}
      {isEditNameModalOpen && (
        <div className={styles.modalOverlay} onClick={() => setIsEditNameModalOpen(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3>Edit Display Name</h3>
            <input
              type="text"
              value={newDisplayName}
              onChange={(e) => setNewDisplayName(e.target.value)}
              placeholder="Enter your display name"
              className={styles.nameInput}
              maxLength={50}
            />
            <div className={styles.modalActions}>
              <button 
                className={styles.cancelButton}
                onClick={() => setIsEditNameModalOpen(false)}
              >
                Cancel
              </button>
              <button 
                className={styles.saveButton}
                onClick={handleSaveName}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
